import { TypedParentMixin } from '../parentMixin';
import { ColorGradingFilter } from '../filters/ColorGradingFilter';
import { Config } from '../../video-data/config';
import { comparer, reaction, ReactiveBase } from '../reactivity';
import { TypedUpdateFlowMixin } from '../updateFlowMixin';
import { MediaAsset } from './MediaAsset';

type GradingState = { source: string; intensity: number } | 'unavailable' | 'ungraded';

type ColorGradingManagerSettings = { parent: MediaAsset };

export class ColorGradingManager extends TypedParentMixin(TypedUpdateFlowMixin(ReactiveBase)) {
    constructor(settings: ColorGradingManagerSettings) {
        super(settings);

        this.addReaction(
            reaction(
                () => ColorGradingManager.#gradingState(this),
                (state) => {
                    if (state === 'unavailable') {
                        return;
                    }
                    if (state === 'ungraded') {
                        // oxlint-disable-next-line mobx/effect-reads-through-this -- a PIXI.Container, so not reactive state — Scene.helpers declares the same thing in its expression
                        const filter = this.#colorGradingFilter;
                        if (!filter) {
                            return;
                        }
                        filter.removeListener('onLoaded', this.#emitAssetLoaded);
                        this.parent.removeFilter(filter.name);
                        this.#emitAssetLoaded();
                        return;
                    }

                    const existingFilter = this.#colorGradingFilter;
                    const filter =
                        existingFilter ??
                        this.parent.addFilter(
                            Config.COLOR_GRADING_FILTER,
                            MediaAsset.COLOR_GRADING_FILTER_NAME,
                        );
                    if (!existingFilter) {
                        filter.addListener('onLoaded', this.#emitAssetLoaded);
                    }
                    if (filter.textureUrl !== state.source) {
                        filter.textureUrl = state.source;
                    }
                    if (filter.intensity !== state.intensity) {
                        filter.intensity = state.intensity;
                    }
                },
                { equals: comparer.shallow, name: 'ColorGradingManager.filter' },
            ),
        );
    }

    override get parent(): MediaAsset {
        return super.parent as MediaAsset;
    }

    static #gradingState(manager: ColorGradingManager): GradingState {
        const mediaAsset = manager.parent;
        if (!mediaAsset || mediaAsset.destroyed) {
            return 'unavailable';
        }
        const settings = mediaAsset._grading;
        if (!settings) {
            return 'ungraded';
        }
        return { source: settings.source, intensity: settings.intensity };
    }

    get #colorGradingFilter(): ColorGradingFilter | undefined {
        const filter = this.parent.getFilterByName(MediaAsset.COLOR_GRADING_FILTER_NAME);
        if (!(filter instanceof ColorGradingFilter)) {
            return undefined;
        }
        return filter;
    }

    #emitAssetLoaded = () => {
        this.emit('onLoaded');
    };

    get isLoaded(): boolean {
        if (!this.parent._grading) {
            return true; // No grading settings means no filter to load
        }
        const filter = this.#colorGradingFilter;
        return filter ? filter.isLoaded : true;
    }
}
